/**
 * Lazy UpgradeModal wrapper
 *
 * Defers loading `@stripe/stripe-js` and `@stripe/react-stripe-js` (pulled in by
 * UpgradeModal) until the modal is opened for the first time. Public browse
 * pages and the global UpgradeModalProvider stay on the critical path without
 * shipping Stripe checkout code on every first paint (PBI #1137).
 *
 * Business rule: every production mount of UpgradeModal must go through this
 * wrapper (or an equivalent dynamic import). Direct static imports of
 * UpgradeModal re-introduce Stripe on the browse bundle — enforced by
 * `src/test/noEagerStripeImport.test.ts`.
 */

import { Suspense, lazy, useEffect, useState } from 'react';
import type { UpgradeModalProps } from './UpgradeModal';

const UpgradeModalLazy = lazy(() =>
  import('./UpgradeModal').then((m) => ({ default: m.UpgradeModal })),
);

/**
 * Renders nothing until `opened` becomes true once, then keeps the lazy chunk
 * mounted so subsequent opens do not re-fetch. Suspense fallback is null —
 * UpgradeModal owns the modal chrome (including preparingCheckout loader)
 * once the chunk resolves.
 */
export function LazyUpgradeModal(props: UpgradeModalProps) {
  const [shouldLoad, setShouldLoad] = useState(props.opened);

  useEffect(() => {
    if (props.opened) {
      setShouldLoad(true);
    }
  }, [props.opened]);

  if (!shouldLoad) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <UpgradeModalLazy {...props} />
    </Suspense>
  );
}
